import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/layout/Header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trophy, Fish, Award, Calendar, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useEffect } from "react";
import { trackEvent, AnalyticsEvents } from "@/lib/analytics";

interface LeaderboardEntry {
  id: string;
  fish_species: string;
  weight_lbs: number;
  date_caught: string;
  photo_url: string | null;
  angler_id: string;
  profiles: {
    angler_name: string;
    nickname: string | null;
    avatar_url: string | null;
  };
}

type TimePeriod = "all" | "month" | "year";

const fetchLeaderboard = async (timePeriod: TimePeriod) => {
  let query = supabase
    .from("catches")
    .select(`
      id,
      fish_species,
      weight_lbs,
      date_caught,
      photo_url,
      angler_id,
      profiles (
        angler_name,
        nickname,
        avatar_url
      )
    `)
    .eq("status", "verified");

  // Apply time filter
  if (timePeriod === "month") {
    const monthAgo = new Date();
    monthAgo.setMonth(monthAgo.getMonth() - 1);
    query = query.gte("date_caught", monthAgo.toISOString());
  } else if (timePeriod === "year") {
    const yearAgo = new Date();
    yearAgo.setFullYear(yearAgo.getFullYear() - 1);
    query = query.gte("date_caught", yearAgo.toISOString());
  }

  query = query.order("weight_lbs", { ascending: false });

  const { data, error } = await query;

  if (error) throw error;
  return data || [];
};

export default function Leaderboard() {
  const navigate = useNavigate();
  const [selectedSpecies, setSelectedSpecies] = useState<string>("all");
  const [timePeriod, setTimePeriod] = useState<TimePeriod>("all");

  useEffect(() => {
    trackEvent({ eventName: AnalyticsEvents.LEADERBOARD_VIEW });
  }, []);

  const { data: catches = [], isLoading, error, refetch } = useQuery({
    queryKey: ["leaderboard", timePeriod],
    queryFn: () => fetchLeaderboard(timePeriod),
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    refetchOnWindowFocus: false,
  });

  const species = [...new Set(catches.map(c => c.fish_species))];

  const getFilteredCatches = () => {
    if (selectedSpecies === "all") return catches;
    return catches.filter(c => c.fish_species === selectedSpecies);
  };

  const getMedalIcon = (index: number) => {
    if (index === 0) return <Trophy className="h-6 w-6 text-yellow-500" />;
    if (index === 1) return <Trophy className="h-6 w-6 text-gray-400" />;
    if (index === 2) return <Trophy className="h-6 w-6 text-amber-700" />;
    return <span className="text-muted-foreground font-semibold">#{index + 1}</span>;
  };

  const topBySpecies = species.map(sp => {
    const speciesCatches = catches.filter(c => c.fish_species === sp);
    return {
      species: sp,
      topCatch: speciesCatches[0],
      count: speciesCatches.length
    };
  }).filter(s => s.topCatch);

  const handleRefresh = () => {
    refetch();
    toast.success("Leaderboard refreshed");
  };

  const LeaderboardSkeleton = () => (
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-48" />
      </CardHeader>
      <CardContent className="space-y-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex items-center gap-4 p-4 rounded-lg border">
            <Skeleton className="h-12 w-12 rounded-full" />
            <Skeleton className="h-12 w-12 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-24" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-6 w-20" />
              <Skeleton className="h-3 w-16" />
            </div>
            <Skeleton className="h-16 w-16 rounded" />
          </div>
        ))}
      </CardContent>
    </Card>
  );

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container py-8">
          <Card className="max-w-md mx-auto">
            <CardContent className="pt-6 text-center space-y-4">
              <Trophy className="h-16 w-16 text-muted-foreground mx-auto" />
              <div>
                <h3 className="text-lg font-semibold mb-2">Failed to Load</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {error instanceof Error ? error.message : "Unknown error"}
                </p>
                <Button onClick={() => refetch()} className="gap-2">
                  <RefreshCw className="h-4 w-4" />
                  Try Again
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container py-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-8">
          <div className="flex items-center gap-3">
            <Trophy className="h-10 w-10 text-primary" />
            <h1 className="text-4xl font-bold text-gradient">Leaderboard</h1>
          </div>
          
          <div className="flex items-center gap-3">
            <Calendar className="h-5 w-5 text-muted-foreground" />
            <Select value={timePeriod} onValueChange={(value) => setTimePeriod(value as TimePeriod)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
                <SelectItem value="year">This Year</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              size="icon"
              onClick={handleRefresh}
              disabled={isLoading}
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {isLoading ? (
          <LeaderboardSkeleton />
        ) : (
        <Tabs defaultValue="all" className="space-y-6">
          <TabsList className="grid w-full max-w-2xl grid-cols-3">
            <TabsTrigger value="all">All Catches</TabsTrigger>
            <TabsTrigger value="species">By Species</TabsTrigger>
            <TabsTrigger value="filter">Filter</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="h-5 w-5 text-primary" />
                  Top Catches - All Species
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {getFilteredCatches().slice(0, 20).map((entry, index) => (
                  <div
                    key={entry.id}
                    className="flex items-center gap-4 p-4 rounded-lg border bg-card hover:bg-accent/5 transition-colors cursor-pointer"
                    onClick={() => navigate(`/catch/${entry.id}`)}
                  >
                    <div className="w-12 flex justify-center">
                      {getMedalIcon(index)}
                    </div>
                    
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={entry.profiles?.avatar_url || undefined} />
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {(entry.profiles?.nickname || entry.profiles?.angler_name)?.charAt(0) || 'A'}
                      </AvatarFallback>
                    </Avatar>

                    <div className="flex-1">
                      <p className="font-semibold">{entry.profiles?.nickname || entry.profiles?.angler_name}</p>
                      <p className="text-sm text-muted-foreground">{entry.fish_species}</p>
                    </div>

                    <div className="text-right">
                      <p className="text-2xl font-bold text-primary">{entry.weight_lbs} lbs</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(entry.date_caught).toLocaleDateString()}
                      </p>
                    </div>

                    {entry.photo_url && (
                      <img 
                        src={entry.photo_url} 
                        alt="Catch"
                        className="h-16 w-16 rounded object-cover"
                      />
                    )}
                  </div>
                ))}

                {getFilteredCatches().length === 0 && (
                  <p className="text-center text-muted-foreground py-8">No verified catches yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="species" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {topBySpecies.map(({ species, topCatch, count }) => (
                <Card 
                  key={species} 
                  className="overflow-hidden cursor-pointer hover:shadow-lg transition-all"
                  onClick={() => navigate(`/catch/${topCatch.id}`)}
                >
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Fish className="h-5 w-5 text-primary" />
                        {species}
                      </span>
                      <Badge variant="secondary">{count} catches</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {topCatch.photo_url && (
                      <img 
                        src={topCatch.photo_url} 
                        alt={species}
                        className="w-full h-32 object-cover rounded"
                      />
                    )}
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={topCatch.profiles?.avatar_url || undefined} />
                        <AvatarFallback className="bg-primary text-primary-foreground">
                          {(topCatch.profiles?.nickname || topCatch.profiles?.angler_name)?.charAt(0) || 'A'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <p className="font-semibold text-sm">{topCatch.profiles?.nickname || topCatch.profiles?.angler_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(topCatch.date_caught).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-center p-3 bg-primary/10 rounded">
                      <p className="text-3xl font-bold text-primary">{topCatch.weight_lbs}</p>
                      <p className="text-xs text-muted-foreground">pounds</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {topBySpecies.length === 0 && (
              <Card>
                <CardContent className="py-8">
                  <p className="text-center text-muted-foreground">No verified catches yet</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="filter" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Filter by Species</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  <Badge
                    variant={selectedSpecies === "all" ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => setSelectedSpecies("all")}
                  >
                    All Species
                  </Badge>
                  {species.map(sp => (
                    <Badge
                      key={sp}
                      variant={selectedSpecies === sp ? "default" : "outline"}
                      className="cursor-pointer"
                      onClick={() => setSelectedSpecies(sp)}
                    >
                      {sp}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="h-5 w-5 text-primary" />
                  {selectedSpecies === "all" ? "All Catches" : `${selectedSpecies} Catches`}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {getFilteredCatches().slice(0, 20).map((entry, index) => (
                  <div
                    key={entry.id}
                    className="flex items-center gap-4 p-4 rounded-lg border bg-card hover:bg-accent/5 transition-colors cursor-pointer"
                    onClick={() => navigate(`/catch/${entry.id}`)}
                  >
                    <div className="w-12 flex justify-center">
                      {getMedalIcon(index)}
                    </div>
                    
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={entry.profiles?.avatar_url || undefined} />
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {(entry.profiles?.nickname || entry.profiles?.angler_name)?.charAt(0) || 'A'}
                      </AvatarFallback>
                    </Avatar>

                    <div className="flex-1">
                      <p className="font-semibold">{entry.profiles?.nickname || entry.profiles?.angler_name}</p>
                      <p className="text-sm text-muted-foreground">{entry.fish_species}</p>
                    </div>

                    <div className="text-right">
                      <p className="text-2xl font-bold text-primary">{entry.weight_lbs} lbs</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(entry.date_caught).toLocaleDateString()}
                      </p>
                    </div>

                    {entry.photo_url && (
                      <img 
                        src={entry.photo_url} 
                        alt="Catch"
                        className="h-16 w-16 rounded object-cover"
                      />
                    )}
                  </div>
                ))}

                {getFilteredCatches().length === 0 && (
                  <p className="text-center text-muted-foreground py-8">No catches found</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        )}
      </main>
    </div>
  );
}
